// Registratore a prova di crash: chunk da 20 s → IndexedDB → upload con retry.
// L'audio è al sicuro localmente PRIMA di qualsiasi tentativo di rete.
import { api } from './api.js';
import { chunksFor, markSent, saveChunk } from './idb.js';

const CHUNK_MS = 20_000;

export function pickMime(): string {
  const candidates = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) return c;
  }
  return '';
}

export type RecorderEvents = {
  onLevel?: (level: number) => void;       // 0..1 per il misuratore
  onTick?: (elapsedS: number) => void;
  onSilence?: (silentForS: number) => void; // silenzio continuo rilevato (dopo il parlato)
  onError?: (message: string) => void;
};

/** Parametri del rilevatore di voce (VAD). I default riproducono il comportamento storico. */
export type VadOptions = {
  threshold?: number;      // RMS sopra cui è parlato (default 0.02)
  adaptiveFloor?: boolean; // stima il rumore di fondo nei primi floorMs e alza la soglia
  floorFactor?: number;    // soglia = max(threshold, floorFactor × floor) (default 2.5)
  floorMs?: number;        // finestra di stima del rumore (default 500)
  minSpeechMs?: number;    // parlato cumulativo minimo perché conti come "ha parlato" (default 0)
  tickMs?: number;         // periodo del misuratore (default 150)
};

/** Stream e AudioContext esterni: chi li passa ne resta proprietario (stop() non li chiude).
 *  Serve alla telefonata: un solo getUserMedia e un solo AudioContext per tutta la chiamata. */
export type RecorderOptions = {
  stream?: MediaStream;
  ctx?: AudioContext;
  vad?: VadOptions;
};

// La voce di mamma ha la precedenza sulla rete. Il caricatore dei video (lib/video-upload.ts)
// non manda un pezzo finché qui c'è un registratore vivo, da qualunque schermata (il quaderno,
// «Correggi a voce», una risposta). Il conto scende solo DOPO la barriera di stop()/discard():
// finché l'ultimo pezzo della sua voce non è arrivato al server, la linea resta sua.
let vivi = 0;
/** Quanti registratori stanno ancora usando microfono o rete. */
export function registratoriAttivi(): number {
  return vivi;
}
/** Una sessione di voce comincia: chi sta usando la rete per altro (un video) si ferma subito. */
export const EVENTO_VOCE = 'memorie:voce';
export function annunciaVoce(): void {
  window.dispatchEvent(new CustomEvent(EVENTO_VOCE));
}

export class Recorder {
  recordingId: string | null = null;
  mime = pickMime();
  private stream: MediaStream | null = null;
  private rec: MediaRecorder | null = null;
  private ctx: AudioContext | null = null;
  private seq = 0;
  private startedAt = 0;
  private pausedMs = 0;
  private pauseStart = 0;
  private meterTimer: number | null = null;
  private uploads: Promise<void>[] = [];
  private failedSeqs: Array<{ seq: number; blob: Blob }> = [];
  private silentSince: number | null = null;
  private speechSeen = false;
  private speechMs = 0;
  private floor = 0;
  private peak = 0;
  private source: MediaStreamAudioSourceNode | null = null;
  private ownsStream = true;
  private ownsCtx = true;
  private discarded = false;
  private wakeLock: WakeLockSentinel | null = null;
  private contato = false;
  stopped = false;

  constructor(private events: RecorderEvents = {}, private opts: RecorderOptions = {}) {}

  private prendi(): void {
    if (this.contato) return;
    this.contato = true;
    vivi++;
  }
  private rilascia(): void {
    if (!this.contato) return;
    this.contato = false;
    vivi = Math.max(0, vivi - 1);
  }

  async start(opts: { purpose: string; sessionId?: number; photoMediaId?: number }): Promise<void> {
    this.prendi();
    annunciaVoce();
    try {
      await this.avvia(opts);
    } catch (err) {
      // Niente registrazione: il microfono si spegne e la linea torna libera.
      this.cleanupHardware();
      this.rilascia();
      throw err;
    }
  }

  private async avvia(opts: { purpose: string; sessionId?: number; photoMediaId?: number }): Promise<void> {
    this.ownsStream = !this.opts.stream;
    this.stream = this.opts.stream ?? await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });
    try {
      const { recordingId } = await api.post<{ recordingId: string }>('/api/recordings', {
        purpose: opts.purpose,
        sessionId: opts.sessionId,
        photoMediaId: opts.photoMediaId,
        mime: this.mime || 'audio/webm',
      });
      this.recordingId = recordingId;
    } catch (err) {
      // Il microfono non deve restare acceso se il server non risponde.
      this.cleanupHardware();
      throw err;
    }
    this.seq = 0;
    this.stopped = false;
    this.startedAt = Date.now();
    this.pausedMs = 0;

    this.rec = new MediaRecorder(this.stream, this.mime ? { mimeType: this.mime } : undefined);
    // La promise dell'INTERO handleChunk entra in uploads in modo SINCRONO:
    // così la barriera di stop() copre sempre anche l'ultimo chunk.
    this.rec.ondataavailable = (e) => {
      if (this.discarded) return; // turno scartato (es. solo silenzio): nulla parte
      if (e.data && e.data.size > 0) this.uploads.push(this.handleChunk(e.data));
    };
    this.rec.onerror = () => this.events.onError?.('Problema con il microfono.');
    this.rec.start(CHUNK_MS);
    this.startMeter();
    // Schermo acceso finché si registra: l'iPad non si addormenta a metà racconto. Facoltativo.
    if (this.ownsStream && 'wakeLock' in navigator) {
      try { this.wakeLock = await navigator.wakeLock.request('screen'); } catch { this.wakeLock = null; }
      // Se nel frattempo è già arrivato stop()/discard(), il lock non deve sopravvivere al microfono.
      if (this.stream === null) { void this.wakeLock?.release().catch(() => undefined); this.wakeLock = null; }
    }
  }

  private async handleChunk(blob: Blob): Promise<void> {
    const recId = this.recordingId;
    if (!recId) return;
    const seq = this.seq++;
    // Upload e salvataggio locale partono insieme: nessuno dei due aspetta l'altro.
    const idbSave = saveChunk({ recId, seq, blob, sent: false }).catch(() => undefined);
    await Promise.all([this.uploadChunk(recId, seq, blob), idbSave]);
  }

  private async uploadChunk(recId: string, seq: number, blob: Blob, attempt = 0): Promise<void> {
    try {
      const res = await fetch(`/api/recordings/${recId}/chunks/${seq}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: blob,
      });
      if (!res.ok) throw new Error(String(res.status));
      void markSent(recId, seq, blob);
    } catch {
      if (attempt < 4) {
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
        return this.uploadChunk(recId, seq, blob, attempt + 1);
      }
      this.failedSeqs.push({ seq, blob });
    }
  }

  private startMeter(): void {
    if (!this.stream) return;
    try {
      this.ownsCtx = !this.opts.ctx;
      this.ctx = this.opts.ctx ?? new AudioContext();
      // iOS crea il contesto "suspended" se nasce fuori da un gesto: senza resume
      // livello e VAD restano a zero e il turno non si chiude mai da solo.
      if (this.ctx.state === 'suspended') void this.ctx.resume().catch(() => undefined);
      this.source = this.ctx.createMediaStreamSource(this.stream);
      const analyser = this.ctx.createAnalyser();
      analyser.fftSize = 512;
      this.source.connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);

      const vad = this.opts.vad ?? {};
      const baseThreshold = vad.threshold ?? 0.02;
      const floorFactor = vad.floorFactor ?? 2.5;
      const floorMs = vad.floorMs ?? 500;
      const minSpeechMs = vad.minSpeechMs ?? 0;
      const tickMs = vad.tickMs ?? 150;
      const meterStart = Date.now();
      const floorSamples: number[] = [];
      let lastTick = meterStart;

      const loop = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = ((buf[i] ?? 128) - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        const now = Date.now();
        const dt = now - lastTick;
        lastTick = now;
        if (rms > this.peak) this.peak = rms;
        this.events.onLevel?.(Math.min(1, rms * 4));
        const paused = this.rec?.state === 'paused';
        if (!paused) {
          let threshold = baseThreshold;
          let calibrating = false;
          if (vad.adaptiveFloor) {
            if (now - meterStart < floorMs) {
              // Rumore di fondo: mediana dei primi campioni (robusta a un colpo o a chi
              // inizia a parlare subito). In questa finestra il parlato non si valuta.
              floorSamples.push(rms);
              const sorted = [...floorSamples].sort((a, b) => a - b);
              this.floor = sorted[Math.floor(sorted.length / 2)] ?? 0;
              calibrating = true;
            }
            threshold = Math.max(baseThreshold, floorFactor * this.floor);
          }
          if (!calibrating) {
            if (rms > threshold) {
              this.speechMs += dt;
              if (this.speechMs >= minSpeechMs) {
                this.speechSeen = true;
                this.silentSince = null;
              }
            } else if (this.speechSeen) {
              this.silentSince ??= now;
              this.events.onSilence?.((now - this.silentSince) / 1000);
            }
          }
          this.events.onTick?.(this.elapsedS());
        }
      };
      this.meterTimer = window.setInterval(loop, tickMs);
    } catch { /* senza misuratore si registra lo stesso */ }
  }

  /** Vero quando il VAD ha riconosciuto parlato valido (≥ minSpeechMs). */
  get speechDetected(): boolean {
    return this.speechSeen;
  }

  /** Numeri del VAD per la telemetria: rumore stimato, picco, ms di parlato. */
  vadStats(): { floor: number; peak: number; speechMs: number } {
    return { floor: this.floor, peak: this.peak, speechMs: Math.round(this.speechMs) };
  }

  elapsedS(): number {
    if (!this.startedAt) return 0;
    const pausedNow = this.pauseStart ? Date.now() - this.pauseStart : 0;
    return Math.floor((Date.now() - this.startedAt - this.pausedMs - pausedNow) / 1000);
  }

  pause(): void {
    if (this.rec?.state === 'recording') {
      this.rec.pause();
      this.pauseStart = Date.now();
    }
  }

  resume(): void {
    if (this.rec?.state === 'paused') {
      this.rec.resume();
      this.pausedMs += Date.now() - this.pauseStart;
      this.pauseStart = 0;
      this.silentSince = null;
    }
  }

  get paused(): boolean {
    return this.rec?.state === 'paused';
  }

  /** Ferma e attende che tutti i chunk siano arrivati al server. */
  async stop(): Promise<{ recordingId: string; durationS: number; chunkCount: number; allUploaded: boolean }> {
    const recId = this.recordingId;
    if (!recId || !this.rec) throw new Error('Registrazione non attiva');
    this.stopped = true;
    const durationS = this.elapsedS();

    try {
      await new Promise<void>((resolve) => {
        this.rec!.onstop = () => resolve();
        if (this.rec!.state === 'inactive') resolve();
        else this.rec!.stop();
      });
      this.cleanupHardware();

      // L'evento dataavailable finale precede onstop, quindi qui uploads è completo.
      await Promise.allSettled(this.uploads);
      // Ultimo tentativo per i chunk rimasti a terra.
      const failed = [...this.failedSeqs];
      this.failedSeqs = [];
      for (const f of failed) await this.uploadChunk(recId, f.seq, f.blob, 3);
      return {
        recordingId: recId,
        durationS,
        chunkCount: this.seq,
        allUploaded: this.failedSeqs.length === 0,
      };
    } finally {
      // Solo adesso, a voce tutta arrivata (o data per persa), la rete torna libera.
      this.rilascia();
    }
  }

  /** Ferma e butta via: niente upload, niente IndexedDB (turno senza parlato).
   *  I chunk già partiti restano sul server come registrazione mai finalizzata. */
  async discard(): Promise<void> {
    this.discarded = true;
    this.stopped = true;
    if (this.rec && this.rec.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        this.rec!.onstop = () => resolve();
        this.rec!.stop();
      });
    }
    this.cleanupHardware();
    // I pezzi già in viaggio finiscono il loro giro: dopo, la rete torna libera.
    void Promise.allSettled(this.uploads).then(() => this.rilascia());
  }

  cleanupHardware(): void {
    if (this.meterTimer) window.clearInterval(this.meterTimer);
    this.meterTimer = null;
    try { this.source?.disconnect(); } catch { /* già scollegato */ }
    this.source = null;
    if (this.ownsStream) this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    if (this.ownsCtx) void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
    void this.wakeLock?.release().catch(() => undefined);
    this.wakeLock = null;
  }

  resetSilence(): void {
    this.silentSince = null;
    this.speechSeen = false;
    this.speechMs = 0;
  }
}

async function reuploadFromIdb(recId: string): Promise<void> {
  const rows = await chunksFor(recId);
  for (const r of rows) {
    await fetch(`/api/recordings/${recId}/chunks/${r.seq}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: r.blob,
    }).catch(() => undefined);
  }
}

/** Il corpo di `POST /api/recordings/:id/finish`, uno a uno come lo legge il server
 *  (`routes/recordings.ts`). Tipizzato apposta: con `Record<string, unknown>` una chiave
 *  scritta male passava muta — ed è successo: `chunkCount` al posto di `expectedChunks`
 *  spegneva il controllo di completezza, e un racconto poteva salvarsi mozzato senza che
 *  la copia in IndexedDB venisse mai richiamata. */
export type FinishBody = {
  sessionId?: number | null;
  questionId?: number;
  durationS?: number;
  appendToMemoryId?: number;
  /** Quanti pezzi il client ha davvero registrato: se sul disco ce ne sono di meno il
   *  server risponde 409 e `finishRecording` li ricarica da IndexedDB. */
  expectedChunks?: number;
  orphanTurn?: boolean;
};

/**
 * Finalizza una registrazione con verifica di completezza: se il server segnala
 * chunk mancanti, li ricarica dalla copia locale IndexedDB e riprova.
 * Lancia se dopo i tentativi la finalizzazione non riesce (l'audio resta al sicuro
 * sia in IndexedDB sia nei chunk sul server).
 */
export async function finishRecording<T extends { mediaId: number; memoryId?: number }>(
  recordingId: string,
  body: FinishBody,
): Promise<T> {
  for (let attempt = 0; attempt < 4; attempt++) {
    const res = await fetch(`/api/recordings/${recordingId}/finish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) return (await res.json()) as T;
    let code = '';
    try {
      code = ((await res.json()) as { error?: string }).error ?? '';
    } catch { /* risposta senza corpo */ }
    if (code === 'chunks_mancanti' || code === 'nessun_chunk') {
      await reuploadFromIdb(recordingId);
    } else if (code === 'finalizzazione_in_corso' || res.status >= 500) {
      await new Promise((r) => setTimeout(r, 1200 * (attempt + 1)));
    } else {
      throw new Error(`finalizzazione rifiutata: ${res.status} ${code}`);
    }
  }
  throw new Error('finalizzazione non riuscita dopo vari tentativi');
}
